import Layout from "../../components/layout/Layout";

// ============================================================
// Types
// ============================================================
interface StatCardProps {
    icon: React.ReactNode;
    iconBg: string;
    label: string;
    value: string | number;
    badge?: { text: string; tone: "green" | "gray" };
}

interface RecentLaborItem {
    name: string;
    phone: string;
    site: string;
    designation: string;
    shift: "Day" | "Night";
    joiningDate: string;
    dailyRate: string;
    status: "Active" | "Inactive";
}

interface SiteLaborData {
    site: string;
    count: number;
    color: string;
}

interface NewSupervisor {
    name: string;
    site: string;
    department: string;
    joiningDate: string;
    status: "Active" | "Inactive";
}

interface Birthday {
    name: string;
    site: string;
    date: string;
    age: number;
    daysLeft: number;
}

interface WorkAnniversary {
    name: string;
    date: string;
    daysLeft: number;
}

interface PayCycle {
    site: string;
    laborName: string;
    nextPayDate: string;
    daysLeft: number;
    amount: string;
    status: "Upcoming" | "Today" | "Overdue";
}

// ============================================================
// Icons (inline SVG, no extra deps)
// ============================================================
const IconLabor = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const IconCheck = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="8" r="5" />
        <path d="M20 21a8 8 0 0 0-16 0" />
        <path d="M9 8l2 2 4-4" />
    </svg>
);

const IconCalendarOff = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <line x1="7" y1="15" x2="15" y2="21" />
    </svg>
);

const IconWage = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M14.5 9.5a2.5 2.5 0 0 0-2.5-1.5c-1.5 0-2.5 1-2.5 2s1 1.6 2.5 2 2.5 1 2.5 2-1 2-2.5 2a2.5 2.5 0 0 1-2.5-1.5" />
        <line x1="12" y1="6" x2="12" y2="8" />
        <line x1="12" y1="16" x2="12" y2="18" />
    </svg>
);

const IconUserPlus = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <line x1="20" y1="8" x2="20" y2="14" />
        <line x1="17" y1="11" x2="23" y2="11" />
    </svg>
);

const IconUsers = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const IconCake = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L12 6" />
        <path d="M12 10L12 14" />
        <path d="M9 4L15 4" />
        <path d="M3 16L21 16" />
        <path d="M5 16L5 20H19V16" />
        <path d="M7 12L17 12" />
        <path d="M7 12C7 10 9 8 12 8C15 8 17 10 17 12" />
    </svg>
);

const IconCalendar = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const IconGift = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="8" width="18" height="4" rx="1" />
        <path d="M12 8v13" />
        <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
        <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8" />
        <path d="M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
    </svg>
);

const IconEdit = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);

// ============================================================
// Profile avatar — initials pill, cycles through a small palette
// keyed off the name so the same person always gets the same color
// ============================================================
const avatarPalette = [
    { bg: "bg-pink-50", text: "text-pink-600" },
    { bg: "bg-teal-50", text: "text-teal-600" },
    { bg: "bg-blue-50", text: "text-blue-600" },
    { bg: "bg-amber-50", text: "text-amber-600" },
    { bg: "bg-purple-50", text: "text-purple-600" },
];

const getAvatarColors = (name: string) => {
    const sum = name.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return avatarPalette[sum % avatarPalette.length];
};

const ProfileTag = ({ name }: { name: string }) => {
    const initials = name.split(" ").map((p) => p.charAt(0)).join("").slice(0, 2).toUpperCase();
    const colors = getAvatarColors(name);
    return (
        <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center font-semibold text-xs shrink-0`}>
                {initials}
            </div>
            <span className="text-sm font-medium text-gray-900">{name}</span>
        </div>
    );
};

// ============================================================
// Event badge + days-left pill, used by Birthdays / Work Anniversaries
// ============================================================
const EventBadge = ({ text, tone }: { text: string; tone: "pink" | "purple" }) => {
    const variants: Record<string, string> = {
        pink: "bg-pink-50 text-pink-600",
        purple: "bg-purple-50 text-purple-600",
    };
    return <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${variants[tone]}`}>{text}</span>;
};

const DaysLeftPill = ({ days }: { days: number }) => (
    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
        {days} {days === 1 ? "day" : "days"}
    </span>
);

// ============================================================
// Event table — shared shape for Birthdays / Work Anniversaries:
// Employee (avatar), Event badge, Date, Days Left. Header shows
// a live count instead of a "View all" link.
// ============================================================
const EventTable = <T extends { name: string; date: string; daysLeft: number }>({
    title,
    icon,
    iconBg,
    rows,
    eventLabel,
    eventTone,
}: {
    title: string;
    icon: React.ReactNode;
    iconBg: string;
    rows: T[];
    eventLabel: string;
    eventTone: "pink" | "purple";
}) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>{icon}</div>
                <h2 className="font-semibold text-gray-900 text-sm">{title}</h2>
            </div>
            <span className="text-sm text-gray-400">{rows.length} upcoming</span>
        </div>
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-gray-50/80 text-gray-500 text-xs uppercase tracking-wider">
                    <tr>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Employee</th>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Event</th>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Date</th>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Days Left</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {rows.map((r, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/60 transition">
                            <td className="px-5 py-3 whitespace-nowrap">
                                <ProfileTag name={r.name} />
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap">
                                <EventBadge text={eventLabel} tone={eventTone} />
                            </td>
                            <td className="px-5 py-3 text-gray-700 whitespace-nowrap">{r.date}</td>
                            <td className="px-5 py-3 whitespace-nowrap">
                                <DaysLeftPill days={r.daysLeft} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

// ============================================================
// Mock data
// ============================================================
const attendanceBreakdown = [
    { label: "Present", value: 2, color: "#0d9488" },
    { label: "Half Day", value: 1, color: "#3b82f6" },
    { label: "Absent", value: 1, color: "#ef4444" },
    { label: "Leave", value: 0, color: "#8b5cf6" },
];

const laborBySite: SiteLaborData[] = [
    { site: "Site 1", count: 1, color: "#8b5cf6" },
    { site: "Site 2", count: 1, color: "#3b82f6" },
    { site: "Site 4", count: 2, color: "#0d9488" },
];

const recentlyAddedLabor: RecentLaborItem[] = [
    { name: "Ramesh Kumar", phone: "98765 43210", site: "Site 4 — Roofing", designation: "Mason", shift: "Day", joiningDate: "15 Jan 2026", dailyRate: "₹650", status: "Active" },
    { name: "Sunita Devi", phone: "91234 56780", site: "Site 2 — Block A", designation: "Helper", shift: "Day", joiningDate: "01 Feb 2026", dailyRate: "₹600", status: "Active" },
    { name: "Arvind Yadav", phone: "99887 66554", site: "Site 4 — Roofing", designation: "Mason", shift: "Night", joiningDate: "10 Dec 2025", dailyRate: "₹650", status: "Inactive" },
];

// --- New Supervisors ---
const newSupervisors: NewSupervisor[] = [
    { name: "Rajesh Nair", site: "Site 1 — Foundation", department: "Construction", joiningDate: "22 Feb 2026", status: "Active" },
    { name: "Meera Iyer", site: "Site 4 — Roofing", department: "Safety", joiningDate: "15 Feb 2026", status: "Active" },
    { name: "Suresh Reddy", site: "Site 2 — Block A", department: "Quality", joiningDate: "05 Feb 2026", status: "Inactive" },
];

// --- Birthdays ---
const birthdays: Birthday[] = [
    { name: "Amit Kumar", site: "Site 1 — Foundation", date: "28 Feb 2026", age: 34, daysLeft: 5 },
    { name: "Sneha Reddy", site: "Site 4 — Roofing", date: "02 Mar 2026", age: 29, daysLeft: 9 },
    { name: "Deepak Singh", site: "Site 2 — Block A", date: "05 Mar 2026", age: 41, daysLeft: 12 },
    { name: "Kavya Nair", site: "Site 1 — Foundation", date: "10 Mar 2026", age: 26, daysLeft: 17 },
];

// --- Work Anniversaries ---
const workAnniversaries: WorkAnniversary[] = [
    { name: "Rajesh Nair", date: "22 Feb 2026", daysLeft: 3 },
    { name: "Meera Iyer", date: "01 Mar 2026", daysLeft: 8 },
    { name: "Ramesh Kumar", date: "04 Mar 2026", daysLeft: 11 },
    { name: "Sunita Devi", date: "09 Mar 2026", daysLeft: 16 },
];

// --- Pay Cycle ---
const payCycles: PayCycle[] = [
    { site: "Site 1 — Foundation", laborName: "Ramesh Kumar", nextPayDate: "27 Feb 2026", daysLeft: 1, amount: "₹1,45,000", status: "Upcoming" },
    { site: "Site 2 — Block A", laborName: "Sunita Devi", nextPayDate: "28 Feb 2026", daysLeft: 2, amount: "₹98,000", status: "Upcoming" },
    { site: "Site 4 — Roofing", laborName: "Arvind Yadav", nextPayDate: "01 Mar 2026", daysLeft: 3, amount: "₹2,10,000", status: "Upcoming" },
    { site: "Site 3 — Interior", laborName: "Sanjeev", nextPayDate: "25 Feb 2026", daysLeft: 0, amount: "₹76,000", status: "Today" },
];

// ============================================================
// Stat card (top row)
// ============================================================
const accentBadge: Record<NonNullable<StatCardProps["badge"]>["tone"], string> = {
    green: "bg-green-50 text-green-600",
    gray: "bg-gray-100 text-gray-500",
};

const StatCard = ({ icon, iconBg, label, value, badge }: StatCardProps) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white ${iconBg}`}>
            {icon}
        </div>
        <p className="text-xs font-semibold tracking-wide text-gray-400 uppercase mt-4">{label}</p>
        <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl font-bold text-gray-900">{value}</span>
            {badge && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${accentBadge[badge.tone]}`}>
                    {badge.text}
                </span>
            )}
        </div>
    </div>
);

// ============================================================
// Labor by Site — vertical bar chart with percentages
// Fix: bars now sit inside a fixed-height track (h-40) with
// justify-end, so each bar's height is computed against a real
// pixel height instead of an undefined parent height (which is
// why bars were collapsing to the 6px minHeight before).
// ============================================================
const LaborBySiteCard = ({ data }: { data: SiteLaborData[] }) => {
    const total = data.reduce((sum, d) => sum + d.count, 0);
    const max = Math.max(...data.map((d) => d.count), 1);

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 h-full">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h2 className="font-semibold text-gray-900">Labor by Site</h2>
                    <p className="text-xs text-gray-400 mt-0.5">{total} total across all sites</p>
                </div>
                <button className="text-sm text-teal-600 font-medium hover:underline">
                    Download
                </button>
            </div>

            {/* Bar track — fixed height so percentage heights resolve correctly */}
            <div className="h-40 flex items-end justify-around gap-4 px-2">
                {data.map((d) => {
                    const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
                    const barHeightPct = max > 0 ? (d.count / max) * 100 : 0;
                    return (
                        <div key={d.site} className="flex flex-col items-center justify-end h-full flex-1">
                            <span className="text-sm font-bold text-gray-700 mb-1.5">{pct}%</span>
                            <div
                                className="w-full max-w-[40px] rounded-t-md transition-all duration-500"
                                style={{
                                    height: `${barHeightPct}%`,
                                    backgroundColor: d.color,
                                    minHeight: "6px",
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Labels row, aligned under each bar */}
            <div className="flex justify-around gap-4 mt-2 px-2">
                {data.map((d) => (
                    <span key={d.site} className="text-xs text-gray-500 text-center flex-1 truncate">
                        {d.site}
                    </span>
                ))}
            </div>
        </div>
    );
};

// ============================================================
// Donut chart (attendance)
// ============================================================
const DonutChart = ({ data }: { data: { label: string; value: number; color: string }[] }) => {
    const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
    const radius = 70;
    const stroke = 24;
    const circumference = 2 * Math.PI * radius;
    let offsetAcc = 0;

    const sorted = [...data].sort((a, b) => b.value - a.value);

    return (
        <div className="flex flex-col items-center">
            <div className="relative">
                <svg width="180" height="180" viewBox="0 0 180 180">
                    <g transform="rotate(-90 90 90)">
                        {sorted.map((d) => {
                            const fraction = d.value / total;
                            const dash = fraction * circumference;
                            const gap = circumference - dash;
                            const el = (
                                <circle
                                    key={d.label}
                                    cx="90"
                                    cy="90"
                                    r={radius}
                                    fill="none"
                                    stroke={d.color}
                                    strokeWidth={stroke}
                                    strokeDasharray={`${dash} ${gap}`}
                                    strokeDashoffset={-offsetAcc}
                                    strokeLinecap="round"
                                />
                            );
                            offsetAcc += dash;
                            return el;
                        })}
                    </g>
                    <text x="90" y="86" textAnchor="middle" className="text-2xl font-bold fill-gray-900">
                        {total}
                    </text>
                    <text x="90" y="106" textAnchor="middle" className="text-xs fill-gray-400">
                        Total
                    </text>
                </svg>
            </div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
                {sorted.map((d) => (
                    <div key={d.label} className="flex items-center gap-1.5 text-sm text-gray-500">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                        {d.label} ({d.value})
                    </div>
                ))}
            </div>
        </div>
    );
};

// ============================================================
// Table component (reusable)
// ============================================================
interface TableColumn<T> {
    key: keyof T;
    header: string;
    render?: (value: T[keyof T], item: T) => React.ReactNode;
}

const GenericTable = <T extends Record<string, any>>({
    data,
    columns,
    title,
    icon,
    viewAllLink,
}: {
    data: T[];
    columns: TableColumn<T>[];
    title: string;
    icon: React.ReactNode;
    viewAllLink?: string;
}) => {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
                        {icon}
                    </div>
                    <h2 className="font-semibold text-gray-900 text-sm">{title}</h2>
                </div>
                {viewAllLink && (
                    <a href={viewAllLink} className="text-sm text-teal-600 font-medium hover:underline">
                        View all
                    </a>
                )}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50/80 text-gray-500 text-xs uppercase tracking-wider">
                        <tr>
                            {columns.map((col) => (
                                <th key={String(col.key)} className="px-5 py-3 text-left font-medium whitespace-nowrap">
                                    {col.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/60 transition">
                                {columns.map((col) => (
                                    <td key={String(col.key)} className="px-5 py-3 text-gray-700 whitespace-nowrap">
                                        {col.render ? col.render(item[col.key], item) : item[col.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const StatusBadge = ({ status }: { status: "Active" | "Inactive" | "Upcoming" | "Today" | "Overdue" }) => {
    const variants: Record<string, string> = {
        Active: "bg-green-50 text-green-600",
        Inactive: "bg-red-50 text-red-500",
        Upcoming: "bg-blue-50 text-blue-600",
        Today: "bg-red-50 text-red-500",
        Overdue: "bg-red-50 text-red-500",
    };
    return (
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${variants[status] || "bg-gray-50 text-gray-500"}`}>
            {status}
        </span>
    );
};


const DaysLeftBadge = ({ days }: { days: number }) => {
    if (days === 0) {
        return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-500">Today</span>;
    }
    if (days === 1) {
        return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-orange-50 text-orange-600">1 day left</span>;
    }
    return <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-600">{days} days left</span>;
};


const Dashboard = () => {
    return (
            <div className="mt-6 space-y-6">
                <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>

                {/* Top stat cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        icon={<IconLabor />}
                        iconBg="bg-gradient-to-br from-blue-500 to-blue-600"
                        label="Total Labor"
                        value={3}
                    />
                    <StatCard
                        icon={<IconCheck />}
                        iconBg="bg-gradient-to-br from-teal-500 to-emerald-600"
                        label="Present Today"
                        value={2}
                        badge={{ text: "of 3", tone: "green" }}
                    />
                    <StatCard
                        icon={<IconCalendarOff />}
                        iconBg="bg-gradient-to-br from-sky-400 to-blue-500"
                        label="Absent Today"
                        value={1}
                    />
                    <StatCard
                        icon={<IconWage />}
                        iconBg="bg-gradient-to-br from-amber-400 to-orange-500"
                        label="Wages Due Today"
                        value="₹1,900"
                    />
                </div>

                {/* Two charts side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <LaborBySiteCard data={laborBySite} />

                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 h-full">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="font-semibold text-gray-900">Today's Attendance Status</h2>
                            <button className="text-sm text-teal-600 font-medium hover:underline">
                                Download
                            </button>
                        </div>
                        <div className="flex justify-center py-2">
                            <DonutChart data={attendanceBreakdown} />
                        </div>
                    </div>
                </div>

                {/* Recently Added Labor — now a full table: S.No, Name, Phone, Site, Shift, Joining Date, Daily Rate, Status, Actions */}
                <GenericTable
                    data={recentlyAddedLabor.map((item, idx) => ({ sno: idx + 1, ...item }))}
                    title="Recently Added Labor"
                    icon={<IconUserPlus />}
                    viewAllLink="/labor"
                    columns={[
                        { key: "sno", header: "S.No" },
                        {
                            key: "name",
                            header: "Name",
                            render: (val) => <ProfileTag name={val as string} />,
                        },
                        { key: "phone", header: "Phone" },
                        { key: "site", header: "Site" },
                        { key: "shift", header: "Shift" },
                        { key: "joiningDate", header: "Joining Date" },
                        { key: "dailyRate", header: "Daily Rate" },
                        {
                            key: "status",
                            header: "Status",
                            render: (val) => <StatusBadge status={val as "Active" | "Inactive"} />,
                        },
                        {
                            key: "name",
                            header: "Actions",
                            render: () => (
                                <button className="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center hover:bg-teal-100 transition">
                                    <IconEdit />
                                </button>
                            ),
                        },
                    ]}
                />

                {/* Birthdays + Work Anniversaries — side by side, event-table style */}
                {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <EventTable
                        title="Upcoming Birthdays"
                        icon={<IconCake />}
                        iconBg="bg-pink-50 text-pink-600"
                        rows={birthdays}
                        eventLabel="Birthday"
                        eventTone="pink"
                    />
                    <EventTable
                        title="Upcoming Work Anniversaries"
                        icon={<IconGift />}
                        iconBg="bg-purple-50 text-purple-600"
                        rows={workAnniversaries}
                        eventLabel="Work Anniversary"
                        eventTone="purple"
                    />
                </div> */}

                {/* Remaining tables — New Employees removed. Stacked full-width so
                    columns (S.No, Name, Site, Department, Joined, Status, etc.)
                    have room and don't wrap/squeeze like they did in the 2-col grid. */}
                {/* <div className="space-y-6">
                   
                    <GenericTable
                        data={newSupervisors.map((item, idx) => ({ sno: idx + 1, ...item }))}
                        title="New Supervisors"
                        icon={<IconUsers />}
                        viewAllLink="/supervisors"
                        columns={[
                            { key: "sno", header: "S.No" },
                            {
                                key: "name",
                                header: "Name",
                                render: (val) => <ProfileTag name={val as string} />,
                            },
                            { key: "site", header: "Site" },
                            { key: "department", header: "Department" },
                            { key: "joiningDate", header: "Joined" },
                            {
                                key: "status",
                                header: "Status",
                                render: (val) => <StatusBadge status={val as "Active" | "Inactive"} />,
                            },
                        ]}
                    />

                    
                    <GenericTable
                        data={payCycles.map((item, idx) => ({ sno: idx + 1, ...item }))}
                        title="Pay Cycle Schedule"
                        icon={<IconCalendar />}
                        viewAllLink="/payroll"
                        columns={[
                            { key: "sno", header: "S.No" },
                            { key: "site", header: "Site" },
                            {
                                key: "laborName",
                                header: "Labor Name",
                                render: (val) => <ProfileTag name={val as string} />,
                            },
                            { key: "nextPayDate", header: "Next Pay Date" },
                            {
                                key: "daysLeft",
                                header: "Days Left",
                                render: (val) => <DaysLeftBadge days={val as number} />,
                            },
                            { key: "amount", header: "Amount" },
                            {
                                key: "status",
                                header: "Status",
                                render: (val) => <StatusBadge status={val as "Upcoming" | "Today" | "Overdue"} />,
                            },
                        ]}
                    />
                </div> */}
            </div>
    );
};

export default Dashboard;